import { Icon } from '@openeducation/oe-rn-components';
import Color from 'color';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import { useColors } from '../../hooks';
import type { ColorsType } from '../../interfaces';
import ThemedText2 from '../themed-text-2';

export type HintButtonType = 'hint' | 'answer';

export interface HintButtonProps {
  onPress?: () => void;
  containerStyle?: StyleProp<ViewStyle>;
  colorType?: keyof ColorsType | undefined;
  type?: HintButtonType;
}

export function HintButton({
  onPress,
  containerStyle,
  colorType = 'onPrimary',
  type = 'hint',
}: HintButtonProps) {
  const colors = useColors();
  const { t } = useTranslation();

  const color = colors[colorType] || colors.onPrimary!;
  const lineColor = Color(color).alpha(0.4).string();

  return (
    <View style={[styles.container, containerStyle]}>
      <LinearGradient
        start={[1, 0]}
        end={[0, 0]}
        colors={[lineColor, '#ffffff00', '#ffffff00']}
        style={styles.line}
      />
      <TouchableOpacity
        accessibilityRole="button"
        onPress={onPress}
        hitSlop={{ top: 8, right: 12, bottom: 8, left: 12 }}
        style={[styles.button, { borderColor: color }]}
      >
        <Icon
          color={color}
          size={16}
          name={type === 'hint' ? 'magic' : 'hand-sparkles'}
          type="solid"
        />
        <ThemedText2 fontSize={16} style={styles.text} type={colorType}>
          {t(`button.${type}`)}
        </ThemedText2>
      </TouchableOpacity>
      <LinearGradient
        start={[0, 0]}
        end={[1, 0]}
        colors={[lineColor, '#ffffff00', '#ffffff00']}
        style={styles.line}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  line: {
    flex: 1,
    height: 1,
  },
  button: {
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingVertical: 5,
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: {
    marginLeft: 8,
  },
});
